<template>
  <div class="rel-dash">
    <div class="rel-dash__actions bloco">
      <p class="fonte-fraca rel-dash__meta">
        Semana <strong>{{ payload?.semana ?? "—" }}</strong>
        · {{ payload?.period?.startDt }} até {{ payload?.period?.endDt }}
      </p>
      <button type="button" class="acao-secundaria" :disabled="!payload || exporting" @click="baixarPdf">
        {{ exporting ? "Gerando PDF…" : "Baixar PDF" }}
      </button>
    </div>

    <div v-if="!payload" class="bloco rel-dash__vazio alinha-centro fonte-fraca">Nenhum dado para o período.</div>

    <div v-else id="relatorio-indicadores-injecao-pdf" ref="pdfRoot" class="rel-dash__conteudo">
      <header class="rel-dash__capa">
        <h2>DADOS PRODUÇÃO INJEÇÃO</h2>
        <p>SEMANA {{ payload.semana }}</p>
      </header>

      <!-- Total geral (página TOTAL do PDF) -->
      <section class="bloco rel-dash__section">
        <h3 class="rel-dash__sec-titulo">Total</h3>
        <div class="grid-4 rel-dash__kpis">
          <article v-for="kpi in kpisTotaisGerais" :key="kpi.label" class="rel-dash__kpi">
            <span class="rel-dash__kpi-label">{{ kpi.label }}</span>
            <strong class="rel-dash__kpi-valor">{{ kpi.valor }}</strong>
          </article>
        </div>
      </section>

      <!-- Por injetora -->
      <section v-for="inj in payload.injetoras" :key="inj.maquinaId" class="bloco rel-dash__section">
        <h3 class="rel-dash__sec-titulo rel-dash__sec-titulo--maq">{{ inj.maquinaNome }}</h3>

        <div class="grid-4 rel-dash__kpis">
          <article class="rel-dash__kpi">
            <span class="rel-dash__kpi-label">Refugo (kg)</span>
            <strong class="rel-dash__kpi-valor">{{ formatKg(inj.totais.refugoKg) }}</strong>
          </article>
          <article class="rel-dash__kpi">
            <span class="rel-dash__kpi-label">Peças boas</span>
            <strong class="rel-dash__kpi-valor">{{ formatInt(inj.totais.pecasBoas) }}</strong>
          </article>
          <article class="rel-dash__kpi">
            <span class="rel-dash__kpi-label">Perda</span>
            <strong class="rel-dash__kpi-valor">{{ formatDecimal(inj.totais.mediaPerdaPct, 2) }}%</strong>
          </article>
          <article class="rel-dash__kpi">
            <span class="rel-dash__kpi-label">Total ABS triturado (kg)</span>
            <strong class="rel-dash__kpi-valor">{{ formatKg(inj.totais.kgAbsTriturado) }}</strong>
          </article>
        </div>

        <div class="grid-4 rel-dash__graficos-linha">
          <div v-for="graf in graficosMetricas(inj)" :key="graf.id" class="rel-dash__grafico-card">
            <h4>{{ graf.titulo }}</h4>
            <div class="rel-dash__plot rel-dash__plot--sm">
              <Bar :data="graf.data" :options="barOptionsMini" />
            </div>
          </div>
        </div>

        <h4 class="rel-dash__subtitulo">Matérias-primas</h4>
        <div class="rel-dash__table-wrap">
          <table class="tabela rel-dash__tabela">
            <thead>
              <tr>
                <th>Matéria-prima</th>
                <th class="alinha-direita">Peças boas</th>
                <th class="alinha-direita">Refugo (kg)</th>
                <th class="alinha-direita">Borra (kg)</th>
                <th class="alinha-direita">Galho (kg)</th>
                <th class="alinha-direita">Perda (%)</th>
                <th class="alinha-direita">Mat. prima (kg)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in inj.materiais" :key="m.nome">
                <td>{{ m.nome }}</td>
                <td class="alinha-direita">{{ formatInt(m.pecasBoas) }}</td>
                <td class="alinha-direita">{{ formatKg(m.refugoKg) }}</td>
                <td class="alinha-direita">{{ formatKg(m.borraKg) }}</td>
                <td class="alinha-direita">{{ formatKg(m.galhoKg) }}</td>
                <td class="alinha-direita">{{ formatDecimal(m.perdaPct, 2) }}%</td>
                <td class="alinha-direita">{{ formatKg(m.materiaPrimaRecebidaKg) }}</td>
              </tr>
              <tr v-if="!inj.materiais?.length">
                <td colspan="7" class="alinha-centro fonte-fraca">Matéria-prima não identificada na estrutura</td>
              </tr>
            </tbody>
          </table>
        </div>

      </section>

      <!-- Consumo de matéria-prima -->
      <section class="bloco rel-dash__section">
        <h3 class="rel-dash__sec-titulo">Consumo de matéria-prima</h3>
        <div class="grid-3 rel-dash__consumo-grid">
          <div v-for="inj in payload.injetoras" :key="'c-' + inj.maquinaId" class="rel-dash__consumo-card">
            <h4>{{ inj.maquinaNome }}</h4>
            <table class="tabela rel-dash__tabela rel-dash__tabela--compacta">
              <tbody>
                <tr><td>Borra (kg)</td><td class="alinha-direita">{{ formatKg(inj.consumoMateriaPrima.borraKg) }}</td></tr>
                <tr><td>Galho (kg)</td><td class="alinha-direita">{{ formatKg(inj.consumoMateriaPrima.galhoKg) }}</td></tr>
                <tr><td>Refugo (kg)</td><td class="alinha-direita">{{ formatKg(inj.consumoMateriaPrima.refugoKg) }}</td></tr>
                <tr><td>Peças boas (kg)</td><td class="alinha-direita">{{ formatKg(inj.consumoMateriaPrima.pecasBoasKg) }}</td></tr>
                <tr class="rel-dash__linha-total">
                  <td>Matéria-prima recebida (kg)</td>
                  <td class="alinha-direita fonte-forte">{{ formatKg(inj.consumoMateriaPrima.materiaPrimaRecebidaKg) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="payload.totaisGerais?.consumoMateriaPrima" class="rel-dash__consumo-card rel-dash__consumo-card--total">
            <h4>Total geral</h4>
            <table class="tabela rel-dash__tabela rel-dash__tabela--compacta">
              <tbody>
                <tr>
                  <td>Borra (kg)</td>
                  <td class="alinha-direita">{{ formatKg(payload.totaisGerais.consumoMateriaPrima.borraKg) }}</td>
                </tr>
                <tr>
                  <td>Galho (kg)</td>
                  <td class="alinha-direita">{{ formatKg(payload.totaisGerais.consumoMateriaPrima.galhoKg) }}</td>
                </tr>
                <tr>
                  <td>Refugo (kg)</td>
                  <td class="alinha-direita">{{ formatKg(payload.totaisGerais.consumoMateriaPrima.refugoKg) }}</td>
                </tr>
                <tr>
                  <td>Peças boas (kg)</td>
                  <td class="alinha-direita">{{ formatKg(payload.totaisGerais.consumoMateriaPrima.pecasBoasKg) }}</td>
                </tr>
                <tr class="rel-dash__linha-total">
                  <td>Matéria-prima recebida (kg)</td>
                  <td class="alinha-direita fonte-forte">
                    {{ formatKg(payload.totaisGerais.consumoMateriaPrima.materiaPrimaRecebidaKg) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip } from "chart.js";
import { Bar } from "vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { exportElementToPdf } from "@/utils/kpiPdfExport";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, ChartDataLabels);

const COR_REFUGO = "#c94c4c";
const COR_PECAS = "hsl(216 64% 52%)";
const COR_PERDA = "hsl(130 45% 42%)";
const COR_MATPRIMA = "#b8860b";

export default {
  name: "RelatorioIndicadoresInjecaoDashboard",
  components: { Bar },
  props: {
    payload: { type: Object, default: null },
  },
  data() {
    return {
      exporting: false,
      barOptionsMini: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: {
            color: "#fff",
            font: { weight: "bold", size: 9 },
            anchor: "end",
            align: "start",
            formatter: (v) => (v == null || Number(v) === 0 ? "" : this.formatChartVal(v)),
          },
        },
        scales: {
          x: { ticks: { maxRotation: 55, minRotation: 0, font: { size: 9 } } },
          y: { beginAtZero: true, ticks: { font: { size: 9 } } },
        },
      },
    };
  },
  computed: {
    kpisTotaisGerais() {
      const t = this.payload?.totaisGerais;
      if (!t) return [];
      return [
        { label: "Refugo (kg)", valor: this.formatKg(t.refugoKg) },
        { label: "Peças boas", valor: this.formatInt(t.pecasBoas) },
        { label: "Perda média", valor: `${this.formatDecimal(t.mediaPerdaPct, 2)}%` },
        { label: "Total ABS triturado (kg)", valor: this.formatKg(t.kgAbsTriturado) },
      ];
    },
  },
  methods: {
    shortLabel(nome, max = 14) {
      const s = String(nome || "");
      return s.length > max ? `${s.slice(0, max)}…` : s;
    },
    formatChartVal(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return "";
      return n >= 100 ? Math.round(n).toLocaleString("pt-BR") : n.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
    },
    chartSimples(inj, campo, cor, isPct = false) {
      const produtos = inj.produtos || [];
      return {
        labels: produtos.map((p) => this.shortLabel(p.produtoNome)),
        datasets: [
          {
            data: produtos.map((p) => (isPct ? p.perdaPct : p[campo])),
            backgroundColor: cor,
          },
        ],
      };
    },
    graficosMetricas(inj) {
      return [
        { id: "refugo", titulo: "Refugo", data: this.chartSimples(inj, "refugoKg", COR_REFUGO) },
        { id: "pecas", titulo: "Peças boas", data: this.chartSimples(inj, "pecasBoas", COR_PECAS) },
        { id: "perda", titulo: "Perda (%)", data: this.chartSimples(inj, "perdaPct", COR_PERDA, true) },
        {
          id: "matprima",
          titulo: "Matéria-prima (kg)",
          data: this.chartSimples(inj, "materiaPrimaRecebidaKg", COR_MATPRIMA),
        },
      ];
    },
    formatInt(n) {
      return new Intl.NumberFormat("pt-BR").format(Math.round(n ?? 0));
    },
    formatKg(n) {
      return new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(n ?? 0);
    },
    formatDecimal(n, frac) {
      return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: frac,
        maximumFractionDigits: frac,
      }).format(n ?? 0);
    },
    async baixarPdf() {
      if (!this.payload || this.exporting) return;
      this.exporting = true;
      try {
        await this.$nextTick();
        await exportElementToPdf(this.$refs.pdfRoot, `indicadores-producao-injecao-semana-${this.payload.semana ?? "periodo"}.pdf`);
      } catch (e) {
        alert(e?.message || "Não foi possível gerar o PDF.");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.rel-dash {
  display: flex;
  flex-direction: column;
  gap: var(--margem);
  min-width: 0;
}

.rel-dash__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px var(--margem);
}

.rel-dash__meta {
  margin: 0;
}

.rel-dash__conteudo {
  display: flex;
  flex-direction: column;
  gap: var(--margem);
}

.rel-dash__capa {
  text-align: center;
  padding: 16px var(--margem);
  border-bottom: 2px solid var(--cor-primaria);
}

.rel-dash__capa h2 {
  margin: 0 0 4px;
  letter-spacing: 0.06em;
  font-size: 1.15rem;
}

.rel-dash__capa p {
  margin: 0;
  color: var(--cor-fonte-fraca);
}

.rel-dash__section {
  padding: var(--margem);
}

.rel-dash__sec-titulo {
  margin: 0 0 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--cor-separador);
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.rel-dash__sec-titulo--maq {
  color: var(--cor-primaria-forte, var(--cor-primaria));
}

.rel-dash__subtitulo {
  margin: 18px 0 10px;
  font-size: 0.9rem;
  font-weight: 600;
}

.rel-dash__kpis,
.rel-dash__graficos-linha,
.rel-dash__rodape-totais,
.rel-dash__consumo-grid {
  gap: 10px;
  align-items: stretch;
}

.rel-dash__kpi {
  padding: 12px 10px;
  border-radius: 10px;
  background: var(--cor-cinza);
  text-align: center;
  min-height: 72px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.rel-dash__kpi-label {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--cor-fonte-fraca);
  margin-bottom: 6px;
  line-height: 1.25;
}

.rel-dash__kpi-valor {
  font-size: 1.15rem;
  color: var(--cor-fonte-forte);
}

.rel-dash__grafico-card {
  padding: 10px;
  border-radius: 10px;
  background: var(--cor-cinza);
  min-width: 0;
}

.rel-dash__grafico-card h4 {
  margin: 0 0 8px;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--cor-fonte-fraca);
}

.rel-dash__plot {
  position: relative;
}

.rel-dash__plot--sm {
  height: 200px;
}

.rel-dash__table-wrap {
  overflow-x: auto;
  margin: 0 -2px;
}

.rel-dash__tabela {
  width: 100%;
  font-size: 0.88rem;
}

.rel-dash__tabela th,
.rel-dash__tabela td {
  padding: 8px 10px;
  vertical-align: middle;
}

.rel-dash__tabela--compacta td {
  padding: 6px 8px;
}

.rel-dash__total-item {
  padding: 12px 10px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 0 0 1px var(--cor-separador) inset;
  background: var(--cor-bg);
}

.rel-dash__total-item span {
  display: block;
  font-size: 0.72rem;
  color: var(--cor-fonte-fraca);
  text-transform: uppercase;
  margin-bottom: 6px;
}

.rel-dash__total-item strong {
  font-size: 1rem;
}

.rel-dash__consumo-card {
  padding: 12px;
  border-radius: 10px;
  background: var(--cor-cinza);
}

.rel-dash__consumo-card h4 {
  margin: 0 0 10px;
  text-align: center;
  font-size: 0.9rem;
  text-transform: uppercase;
}

.rel-dash__consumo-card--total {
  background: linear-gradient(135deg, var(--cor-primaria-fraca), var(--cor-cinza));
  box-shadow: 0 0 0 1px var(--cor-primaria) inset;
}

.rel-dash__linha-total td {
  border-top: 1px solid var(--cor-separador);
  padding-top: 8px;
}

.rel-dash__vazio {
  padding: 2rem;
}

@media screen and (max-width: 1100px) {
  .rel-dash__graficos-linha {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

@media screen and (max-width: 700px) {
  .rel-dash__kpis,
  .rel-dash__graficos-linha,
  .rel-dash__rodape-totais {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  .rel-dash__consumo-grid {
    grid-template-columns: 1fr !important;
  }
}
</style>
